import type { CSSObject } from "type/tss";
import { Theme } from "@mui/material/styles";

export const styles = (theme: Theme) => {
	return ({
		formLabel: {
			fontWeight: "bold",
		},
		headerRow: {
			display: "flex",
			flexDirection: "row",
			alignItems: "center",
			justifyContent: "space-between",
			// Mobile: stack Map title, then the View Search link beneath it
			// (the page description follows below in the body).
			[theme.breakpoints.down("xs")]: {
				flexDirection: "column",
				alignItems: "flex-start",
				gap: 8,
			},
		},
		introText: {
			color: theme.palette.text.secondary,
			marginBottom: 8,
		},
		sectionHeading: {
			fontWeight: "bold",
			fontSize: 18,
			color: theme.palette.custom.textPrimary,
			marginBottom: 8,
		},
		sectionDescription: {
			color: theme.palette.custom.textPrimary,
			marginBottom: 16,
		},
		sectionDivider: {
			margin: "24px 0",
		},
		zoomWarning: {
			display: "flex",
			alignItems: "flex-start",
			color: theme.palette.text.secondary,
			marginBottom: 16,
		},
		zoomWarningIcon: {
			color: theme.palette.warning.main,
			marginRight: 8,
			flexShrink: 0,
			marginTop: 2,
		},
		// Unsaved-changes modal warning icon — Figma "Icon/toast-warning".
		modalWarningIcon: {
			color: "#9D6506",
			flexShrink: 0,
		},
		mapPreview: {
			width: "100%",
			// The Map's inner container is height:100%, so it needs an explicit
			// height here (the old layout borrowed it from a flex row).
			height: 300,
			// Card content padding is 24 and the heading adds 8 below itself, so add
			// 16 here to make the "Map Preview" title equidistant from the card top
			// and the map (24 above, 24 below).
			marginTop: 16,
		},
		zoomSlider: {
			display: "block",
			height: 2,
			padding: "13px 0",
			marginTop: 4,
			// Dashed (stepped) rail + track to match the design.
			"& .MuiSlider-rail": {
				height: 2,
				opacity: 1,
				backgroundColor: "transparent",
				backgroundImage: `repeating-linear-gradient(to right, ${theme.palette.text.disabled} 0, ${theme.palette.text.disabled} 28px, transparent 28px, transparent 40px)`,
			},
			"& .MuiSlider-track": {
				height: 2,
				border: "none",
				backgroundColor: "transparent",
				backgroundImage: `repeating-linear-gradient(to right, ${theme.palette.primary.main} 0, ${theme.palette.primary.main} 28px, transparent 28px, transparent 40px)`,
			},
			// v5 centers the thumb with translate(-50%, -50%); the v4-era negative margins that
			// used to do that job now shift it off the rail instead (MAX-4177).
			"& .MuiSlider-thumb": {
				width: 14,
				height: 14,
				backgroundColor: theme.palette.primary.main,
			},
		},
		zoomEndLabels: {
			display: "flex",
			justifyContent: "space-between",
			marginTop: 4,
		},
		functionalityOption: {
			marginBottom: 16,
			"&:last-child": {
				marginBottom: 0,
			},
		},
		controlLabel: {
			alignItems: "flex-start",
			marginRight: 0,
			"& .MuiFormControlLabel-label": {
				marginTop: 9,
			},
		},
		controlLabelControl: {
			marginTop: 0,
		},
		optionTitleRow: {
			display: "flex",
			alignItems: "center",
			gap: 8,
		},
		optionTitle: {
			fontWeight: "bold",
		},
		optionDescription: {
			color: theme.palette.custom.textPrimary,
			marginTop: 4,
		},
		fieldDescriptionGroup: {
			maxWidth: 560,
		},
	} satisfies Record<string, CSSObject>);
};
